import {
  type Entity,
  parseEntityRef,
  RELATION_API_CONSUMED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_OWNED_BY,
  RELATION_PROVIDES_API,
  stringifyEntityRef,
} from '@backstage/catalog-model';

/**
 * A component's dependencies, read once and used everywhere they are shown —
 * the topology, the Dependencies tab, the count on its tab and the consumers in
 * the facts bar (specs/component-page.md). One reading, so the four can never
 * disagree: the target picture drew the same relations as 4, as "3 in · 3 out"
 * and as 2 + 2 on one page.
 *
 *   in  = who depends on the component, or calls an API it provides
 *   out = what it depends on, and which APIs it calls
 */

/** Why a related entity is on the list. */
export type RelationVia =
  'dependsOn' | 'consumesApi' | 'dependencyOf' | 'callsItsApi';

export type RelatedEntity = {
  ref: string;
  kind: string;
  name: string;
  title?: string;
  owner?: string;
  lifecycle?: string;
  type?: string;
  via: RelationVia[];
  /** False when the catalog holds no entity under this reference. */
  resolved: boolean;
};

export type ComponentRelations = {
  outbound: RelatedEntity[];
  inbound: RelatedEntity[];
  subcomponents: RelatedEntity[];
};

/** The fields the related entities are read with — enough to list them. */
export const RELATED_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'spec.owner',
  'spec.lifecycle',
  'spec.type',
  'relations',
];

export function targetsOf(entity: Entity, type: string): string[] {
  return (entity.relations ?? [])
    .filter(relation => relation.type === type)
    .map(relation => relation.targetRef);
}

/** Every entity this one names in a relation — what the banner checks. */
export function allTargetsOf(entity: Entity): string[] {
  return [...new Set((entity.relations ?? []).map(r => r.targetRef))];
}

/** The APIs this component provides, and who calls each (from the APIs). */
export function consumersOfProvidedApis(
  entity: Entity,
  found: Map<string, Entity | undefined>,
): string[] {
  return targetsOf(entity, RELATION_PROVIDES_API).flatMap(apiRef => {
    const api = found.get(apiRef);
    return api ? targetsOf(api, RELATION_API_CONSUMED_BY) : [];
  });
}

/**
 * The owner as a full reference. The catalog resolves `spec.owner` into an
 * `ownedBy` relation; the bare `spec.owner: platform` many entries carry would
 * reach a link that cannot tell which kind it names, and fail the row.
 */
export function ownerOf(entity: Entity | undefined): string | undefined {
  if (!entity) return undefined;
  const [related] = targetsOf(entity, RELATION_OWNED_BY);
  if (related) return related;
  const owner = (entity.spec as Record<string, unknown> | undefined)?.owner;
  if (typeof owner !== 'string' || !owner) return undefined;
  try {
    return stringifyEntityRef(
      parseEntityRef(owner, {
        defaultKind: 'group',
        defaultNamespace: 'default',
      }),
    );
  } catch {
    // An owner that is not a reference at all: the row says it has none
    // rather than the whole list failing over one entry.
    return undefined;
  }
}

function describe(
  ref: string,
  found: Map<string, Entity | undefined>,
  via: RelationVia,
): RelatedEntity {
  const entity = found.get(ref);
  const parsed = parseEntityRef(ref);
  const spec = (entity?.spec ?? {}) as Record<string, unknown>;
  return {
    ref,
    kind: entity?.kind ?? parsed.kind,
    name: entity?.metadata.name ?? parsed.name,
    title: entity?.metadata.title,
    owner: ownerOf(entity),
    lifecycle: typeof spec.lifecycle === 'string' ? spec.lifecycle : undefined,
    type: typeof spec.type === 'string' ? spec.type : undefined,
    via: [via],
    resolved: entity !== undefined,
  };
}

/** Merge repeated references into one entry that names every reason. */
function collect(
  entries: [string, RelationVia][],
  found: Map<string, Entity | undefined>,
  self: string,
): RelatedEntity[] {
  const byRef = new Map<string, RelatedEntity>();
  for (const [ref, via] of entries) {
    if (ref === self) continue;
    const known = byRef.get(ref);
    if (known) {
      if (!known.via.includes(via)) known.via.push(via);
    } else {
      byRef.set(ref, describe(ref, found, via));
    }
  }
  return [...byRef.values()].sort((a, b) =>
    (a.title ?? a.name).localeCompare(b.title ?? b.name),
  );
}

/**
 * Sort a component's relations into what it depends on, what depends on it,
 * and its subcomponents.
 *
 * `found` holds every entity the catalog returned for the references asked
 * about; a reference missing from it is kept, marked unresolved — a dependency
 * on something the catalog does not know is still a dependency, and dropping it
 * would hide exactly the defect the banner names.
 */
export function relationsOf(
  entity: Entity,
  found: Map<string, Entity | undefined>,
): ComponentRelations {
  const self = stringifyEntityRef(entity);
  return {
    outbound: collect(
      [
        ...targetsOf(entity, RELATION_DEPENDS_ON).map(
          ref => [ref, 'dependsOn'] as [string, RelationVia],
        ),
        ...targetsOf(entity, RELATION_CONSUMES_API).map(
          ref => [ref, 'consumesApi'] as [string, RelationVia],
        ),
      ],
      found,
      self,
    ),
    inbound: collect(
      [
        ...targetsOf(entity, RELATION_DEPENDENCY_OF).map(
          ref => [ref, 'dependencyOf'] as [string, RelationVia],
        ),
        ...consumersOfProvidedApis(entity, found).map(
          ref => [ref, 'callsItsApi'] as [string, RelationVia],
        ),
      ],
      found,
      self,
    ),
    subcomponents: collect(
      targetsOf(entity, RELATION_HAS_PART)
        .filter(ref => parseEntityRef(ref).kind.toLowerCase() === 'component')
        .map(ref => [ref, 'dependencyOf'] as [string, RelationVia]),
      found,
      self,
    ),
  };
}

/** The references among `asked` the catalog did not return. */
export function unresolvedOf(
  asked: string[],
  found: Map<string, Entity | undefined>,
): string[] {
  return asked.filter(ref => found.get(ref) === undefined);
}
